import * as React from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, Moon, Sun } from "lucide-react";
import { Collapsible } from "@/components/ui/collapsible";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";

export function ThemeToggle() {
  const { isMobile } = useSidebar();
  const [open, setOpen] = React.useState(false);
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const themeOptions = [
    { value: "light", label: t("Light") },
    { value: "dark", label: t("Dark") },
    { value: "system", label: t("System") },
  ];

  const handleThemeChange = (value: string) => {
    setTheme(value as "light" | "dark" | "system");
    setOpen(false);
  };

  const ThemeIcon = theme === "dark" ? Moon : Sun;

  return (
    <Collapsible key="theme-toggle" asChild className="group/collapsible">
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton tooltip={t("Toggle theme")} asChild className="cursor-pointer">
              <div className="flex items-center">
                <ThemeIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground hover:text-primary">{t("Toggle theme")}</span>
              </div>
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-56 rounded-lg p-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {themeOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleThemeChange(option.value)}
                      className="cursor-pointer gap-2 p-2"
                    >
                      <CheckIcon className={cn("mr-2 h-4 w-4", theme === option.value ? "opacity-100" : "opacity-0")} />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </Collapsible>
  );
}
